import { NEW_RELIC_KEY } from "utils/constants";
import { getSessionId } from "utils/session-id";

async function sendLog(message, level, data, error = {}) {
    if (typeof window === "undefined") return;

    const logUrl = `https://log-api.newrelic.com/log/v1?Api-Key=${NEW_RELIC_KEY}`;

    const err = {
        error: error?.message || String(error),
        stack: error?.stack,
        errorResponseData: error?.response?.data,
        errorResponseStatus: error?.response?.status,
    };
    const debugInfo = getDebugInfo();
    const permissions = await debugInfo.permissionsPromise;
    delete debugInfo.permissionsPromise;
    const sessionId = getSessionId();

    try {
        const response = await fetch(logUrl, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify([
                {
                    message,
                    entity: {
                        guid: "NDM1NzkyM3xCUk9XU0VSfEFQUExJQ0FUSU9OfDExMDMzMTE2MzA",
                        guids: "NDM1NzkyM3xCUk9XU0VSfEFQUExJQ0FUSU9OfDExMDMzMTE2MzA",
                        name: "Headless",
                    },
                    environment: "production",
                    timestamp: new Date().toISOString(),
                    sessionId,
                    userInfo: getUserInfo(),
                    level,
                    data,
                    error: err,
                    debugInfo: {
                        ...debugInfo,
                        permissions,
                    }
                },
            ]),
        });

        if (!response.ok) {
            console.error("Failed to send log to New Relic:", response.statusText);
        }
    } catch (error) {
        console.error("Error sending log to New Relic:", error);
    }
}


function getUserInfo() {
    try {
        const userAgent = navigator.userAgent || "Unknown";
        const platform = navigator.platform || "Unknown";
        const language = navigator.language || "Unknown";
        const screenWidth = screen.width || 0;
        const screenHeight = screen.height || 0;

        return {
            os: platform,
            browser: getBrowser(userAgent),
            language: language,
            screenResolution: `${screenWidth}x${screenHeight}`,
            userAgent: userAgent,
        };
    } catch (error) {
        console.error("Error gathering user info:", error);
        return {
            os: "Unknown",
            browser: "Unknown",
            language: "Unknown",
            screenResolution: "Unknown",
            userAgent: "Unknown",
            error: error.message || "Unknown error",
        };
    }
}

function getBrowser(userAgent) {
    try {
        if (userAgent.includes("Chrome") && !userAgent.includes("Edg") && !userAgent.includes("OPR")) {
            return "Chrome";
        } else if (userAgent.includes("Safari") && !userAgent.includes("Chrome")) {
            return "Safari";
        } else if (userAgent.includes("Firefox")) {
            return "Firefox";
        } else if (userAgent.includes("Edg")) {
            return "Edge";
        } else if (userAgent.includes("OPR") || userAgent.includes("Opera")) {
            return "Opera";
        } else if (userAgent.includes("Trident") || userAgent.includes("MSIE")) {
            return "Internet Explorer";
        } else {
            return "Unknown";
        }
    } catch (error) {
        console.error("Error determining browser:", error);
        return "Unknown";
    }
}

function getDebugInfo() {
    try {
        if (typeof window === "undefined") return {};

        const nav = navigator || {};
        const loc = window.location || {};
        const doc = document || {};
        const screenObj = window.screen || {};
        const perf = window.performance || {};
        const timing = perf.timing || {};
        const connection =
            nav.connection || nav.mozConnection || nav.webkitConnection || null;

        // Helpers
        const safe = (fn, fallback = null) => {
            try {
                const v = fn();
                return v === undefined ? fallback : v;
            } catch {
                return fallback;
            }
        };

        const getStorageSupport = () => ({
            localStorage: safe(() => {
                const k = "__test__";
                window.localStorage.setItem(k, "1");
                window.localStorage.removeItem(k);
                return true;
            }, false),
            sessionStorage: safe(() => {
                const k = "__test__";
                window.sessionStorage.setItem(k, "1");
                window.sessionStorage.removeItem(k);
                return true;
            }, false),
            cookiesEnabled: safe(() => nav.cookieEnabled, null),
        });

        const getNetworkInfo = () => ({
            online: safe(() => nav.onLine, null),
            connection: connection
                ? {
                    effectiveType: safe(() => connection.effectiveType, null),
                    downlink: safe(() => connection.downlink, null),
                    rtt: safe(() => connection.rtt, null),
                    saveData: safe(() => connection.saveData, null),
                    type: safe(() => connection.type, null),
                }
                : null,
        });

        const getViewportInfo = () => ({
            viewport: {
                width: safe(() => window.innerWidth, null),
                height: safe(() => window.innerHeight, null),
                devicePixelRatio: safe(() => window.devicePixelRatio, null),
                colorDepth: safe(() => screenObj.colorDepth, null),
                pixelDepth: safe(() => screenObj.pixelDepth, null),
            },
            screen: {
                width: safe(() => screenObj.width, null),
                height: safe(() => screenObj.height, null),
                availWidth: safe(() => screenObj.availWidth, null),
                availHeight: safe(() => screenObj.availHeight, null),
                orientation: safe(() => screenObj.orientation?.type, null),
            },
        });

        const getPageContext = () => ({
            url: safe(() => loc.href, null),
            origin: safe(() => loc.origin, null),
            pathname: safe(() => loc.pathname, null),
            search: safe(() => loc.search, null),
            hash: safe(() => loc.hash, null),
            referrer: safe(() => doc.referrer, null),
            title: safe(() => doc.title, null),
            visibilityState: safe(() => doc.visibilityState, null),
            readyState: safe(() => doc.readyState, null),
            // Helpful for debugging SPA nav issues
            historyLength: safe(() => window.history.length, null),
        });

        const getDeviceInfo = () => ({
            platform: safe(() => nav.platform, null),
            userAgent: safe(() => nav.userAgent, null),
            language: safe(() => nav.language, null),
            languages: safe(() => nav.languages, null),
            hardwareConcurrency: safe(() => nav.hardwareConcurrency, null),
            deviceMemoryGB: safe(() => nav.deviceMemory, null),
            maxTouchPoints: safe(() => nav.maxTouchPoints, null),
            vendor: safe(() => nav.vendor, null),
            doNotTrack: safe(() => nav.doNotTrack, null),
            // Some browsers expose this:
            webdriver: safe(() => nav.webdriver, null),
        });

        const getTimeInfo = () => ({
            timestamp: new Date().toISOString(),
            timezone: safe(
                () => Intl.DateTimeFormat().resolvedOptions().timeZone,
                null
            ),
            timezoneOffsetMinutes: safe(() => new Date().getTimezoneOffset(), null),
        });

        const getPerformanceInfo = () => {
            // Newer PerformanceNavigationTiming
            const navEntries = safe(() => perf.getEntriesByType?.("navigation"), []);
            const navEntry = Array.isArray(navEntries) && navEntries[0] ? navEntries[0] : null;

            const legacyTiming =
                timing && timing.navigationStart
                    ? {
                        navigationStart: timing.navigationStart,
                        domContentLoadedEventEnd: timing.domContentLoadedEventEnd,
                        loadEventEnd: timing.loadEventEnd,
                        responseEnd: timing.responseEnd,
                        domInteractive: timing.domInteractive,
                    }
                    : null;

            return {
                navigation: navEntry
                    ? {
                        type: navEntry.type,
                        startTime: navEntry.startTime,
                        duration: navEntry.duration,
                        redirectCount: navEntry.redirectCount,
                        domContentLoadedEventEnd: navEntry.domContentLoadedEventEnd,
                        loadEventEnd: navEntry.loadEventEnd,
                        responseEnd: navEntry.responseEnd,
                        transferSize: navEntry.transferSize,
                        encodedBodySize: navEntry.encodedBodySize,
                        decodedBodySize: navEntry.decodedBodySize,
                        nextHopProtocol: navEntry.nextHopProtocol,
                    }
                    : null,
                legacyTiming,
                memory: safe(() => {
                    const m = perf.memory; // Chromium-only
                    if (!m) return null;
                    return {
                        jsHeapSizeLimit: m.jsHeapSizeLimit,
                        totalJSHeapSize: m.totalJSHeapSize,
                        usedJSHeapSize: m.usedJSHeapSize,
                    };
                }, null),
            };
        };

        const getFeatureSupport = () => ({
            serviceWorker: safe(() => "serviceWorker" in nav, false),
            geolocation: safe(() => "geolocation" in nav, false),
            clipboard: safe(() => "clipboard" in nav, false),
            webgl: safe(() => {
                const canvas = doc.createElement("canvas");
                return !!(
                    canvas.getContext("webgl") || canvas.getContext("experimental-webgl")
                );
            }, null),
            webgl2: safe(() => {
                const canvas = doc.createElement("canvas");
                return !!canvas.getContext("webgl2");
            }, null),
        });

        const getPermissionsSnapshot = () => {
            // Permissions API is not universal; also some permissions require secure context.
            const perms = ["geolocation", "notifications", "clipboard-read", "clipboard-write", "camera", "microphone"];
            return safe(async () => {
                if (!nav.permissions?.query) return null;
                const results = {};
                await Promise.all(
                    perms.map(async (name) => {
                        try {
                            const status = await nav.permissions.query({ name });
                            results[name] = status?.state || null;
                        } catch {
                            results[name] = null;
                        }
                    })
                );
                return results;
            }, null);
        };

        // Note: getPermissionsSnapshot returns a Promise. If you don’t want async,
        // keep it out of the log payload or resolve it before calling sendLog.
        // I’m returning it as `permissionsPromise` so you can choose.
        return {
            page: getPageContext(),
            device: getDeviceInfo(),
            viewport: getViewportInfo(),
            network: getNetworkInfo(),
            storage: getStorageSupport(),
            time: getTimeInfo(),
            performance: getPerformanceInfo(),
            features: getFeatureSupport(),
            permissionsPromise: getPermissionsSnapshot(), // optional async
        };
    } catch (error) {
        console.error("Error gathering debug info:", error);
        return { error: error.message || "Unknown error" };
    }
}


export default sendLog;
